import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { Save } from 'lucide-react'

import type {
    CreateInNoteItemInput,
    CreateInNoteRequest,
} from '@/lib/api/scenting-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import {
    logEntryToFormValues,
    logEntryToSourceOptions,
} from '@/components/scenting-log/log-entry-prefill'
import { useScentingLogEntry } from '@/hooks/use-scenting-log-query'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { LotSourcesSubTable } from '@/components/lots/lot-sources-subtable'
import {
    useScentingLotSource,
    useSortingLotSource,
} from '@/components/lots/lot-picker-source'
import { useCreateScentingInNote } from '@/hooks/use-scenting-lot-query'
import {
    useSortingWithdrawalNote,
    useSortingWithdrawalNotesInfinite,
} from '@/hooks/use-sorting-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { useTeaGradesList } from '@/hooks/use-tea-grade-query'
import { ApiError } from '@/lib/api/client'
import { todayYMD } from '@/lib/date-presets'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const baseItemFields = {
    // Nguồn tầng dòng (bắt buộc ≥1 khi reason=from_process — kiểm ở superRefine).
    sources: z
        .array(
            z.object({
                source_lot_id: z.string().optional(),
                source_quantity: z.coerce.number().optional(),
            }),
        )
        .optional(),
}

const itemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('new'),
        new_lot_code: z.string().optional(),
        new_lot_grade_id: z
            .string({ required_error: m.form_err_pick_grade() })
            .uuid(m.form_err_pick_grade()),
        // Optional + KHÔNG positive ở field: dòng tách để quantity=0 (mặc định) sẽ
        // fail nếu để .positive() → lỗi ẩn (ô KL đã bị giấu) chặn submit. Ràng buộc
        // "có quantity > 0 khi KHÔNG tách" kiểm ở superRefine (hiện lỗi dưới ô KL).
        quantity: z.coerce
            .number({ invalid_type_error: m.form_err_number_invalid() })
            .optional(),
        // Tách lô: ≥2 lô con (mã optional + kg > 0). Kiểm số lượng/trùng ở superRefine.
        splits: z
            .array(
                z.object({
                    new_lot_code: z.string().optional(),
                    quantity: z.coerce
                        .number({
                            invalid_type_error: m.form_err_number_invalid(),
                        })
                        .positive(m.form_err_quantity_positive()),
                }),
            )
            .optional(),
        ...baseItemFields,
    }),
    z.object({
        kind: z.literal('existing'),
        scenting_lot_id: z
            .string({ required_error: m.form_err_pick_existing_lot() })
            .uuid(m.form_err_pick_existing_lot()),
        quantity: z.coerce
            .number({
                required_error: m.form_err_quantity_required(),
                invalid_type_error: m.form_err_number_invalid(),
            })
            .positive(m.form_err_quantity_positive()),
        ...baseItemFields,
    }),
])

export const schema = z
    .object({
        reason: z.enum([
            'adjustment',
            'from_process',
            'return_from_packaging_process',
        ]),
        // Ngày lấy hương (YYYY-MM-DD) — mặc định hôm nay, người dùng đổi được.
        scenting_date: z.string().min(1, m.scl_new_err_scenting_date()),
        notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
        items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
    })
    .superRefine((data, ctx) => {
        // Tách lô (mọi reason): dòng "lô mới" có splits phải ≥2 lô con, mã không
        // trùng; nếu KHÔNG tách thì bắt buộc có số lượng > 0.
        data.items.forEach((it, idx) => {
            if (it.kind !== 'new') return
            const splits = it.splits ?? []
            if (splits.length > 0) {
                if (splits.length < 2) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'splits'],
                        message: m.lot_items_split_min_hint(),
                    })
                }
                const seen = new Set<string>()
                splits.forEach((s, sIdx) => {
                    if (!s.new_lot_code) return
                    if (seen.has(s.new_lot_code)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                'items',
                                idx,
                                'splits',
                                sIdx,
                                'new_lot_code',
                            ],
                            message: m.lot_items_split_dup_code(),
                        })
                    } else {
                        seen.add(s.new_lot_code)
                    }
                })
            } else if (it.quantity == null || it.quantity <= 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['items', idx, 'quantity'],
                    message: m.form_err_quantity_positive(),
                })
            }
        })

        // Luồng đi chuỗi: mỗi dòng phải khai ≥1 lô phân loại nguồn; mỗi nguồn có
        // lô + kg xuất >= 0 (cho phép 0, cấm âm/để trống); không khai trùng lô.
        if (data.reason !== 'from_process') return
        data.items.forEach((it, idx) => {
            const sources = it.sources ?? []
            if (sources.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['items', idx, 'sources'],
                    message: m.scl_new_err_source_lot(),
                })
                return
            }
            const seen = new Set<string>()
            sources.forEach((s, sIdx) => {
                if (!s.source_lot_id) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'sources', sIdx, 'source_lot_id'],
                        message: m.scl_new_err_source_lot(),
                    })
                } else if (seen.has(s.source_lot_id)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'sources', sIdx, 'source_lot_id'],
                        message: m.scl_new_err_source_dup(),
                    })
                } else {
                    seen.add(s.source_lot_id)
                }
                if (s.source_quantity == null || s.source_quantity < 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [
                            'items',
                            idx,
                            'sources',
                            sIdx,
                            'source_quantity',
                        ],
                        message: m.scl_new_err_source_qty(),
                    })
                }
            })
        })
    })

type FormData = z.infer<typeof schema>
type LotOpt = LotOption

/**
 * buildScentingInItems map item của form → payload API. Tách riêng (pure) để test
 * dễ: dòng tách gửi `splits` (bỏ `quantity` đơn), dòng thường gửi `quantity`.
 * `isChain` (reason=from_process) thì đính kèm `sources`.
 */
export function buildScentingInItems(
    items: FormData['items'],
    isChain: boolean,
): CreateInNoteItemInput[] {
    return items.map((it) => {
        const src = isChain
            ? {
                  sources: (it.sources ?? [])
                      .filter((s) => s.source_lot_id)
                      .map((s) => ({
                          source_lot_id: s.source_lot_id as string,
                          source_quantity: Number(s.source_quantity ?? 0),
                      })),
              }
            : {}
        if (it.kind === 'new') {
            const splits = it.splits ?? []
            if (splits.length > 0) {
                return {
                    new_lot_grade_id: it.new_lot_grade_id,
                    splits: splits.map((s) => ({
                        new_lot_code: s.new_lot_code || undefined,
                        quantity: Number(s.quantity),
                    })),
                    ...src,
                }
            }
            return {
                new_lot_grade_id: it.new_lot_grade_id,
                new_lot_code: it.new_lot_code || undefined,
                quantity: Number(it.quantity),
                ...src,
            }
        }
        return {
            scenting_lot_id: it.scenting_lot_id,
            quantity: Number(it.quantity),
            ...src,
        }
    })
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/stock-in-notes/new',
)({
    // logEntryId: mở từ một dòng sổ lấy hương (#M-16) → form điền sẵn theo dòng đó.
    // pxk: đường vào cũ từ màn PXK kho phân loại — giữ lại để link sẵn có không vỡ.
    // Kiểu trả về khai optional tường minh, nếu không mọi <Link> tới route này
    // đều bị bắt buộc truyền prop search.
    validateSearch: (
        search: Record<string, unknown>,
    ): { logEntryId?: string; pxk?: string } => ({
        logEntryId:
            typeof search.logEntryId === 'string'
                ? search.logEntryId
                : undefined,
        pxk: typeof search.pxk === 'string' ? search.pxk : undefined,
    }),
    component: NewPNKPage,
})

function NewPNKPage() {
    const navigate = useNavigate()
    const create = useCreateScentingInNote()
    const { logEntryId } = Route.useSearch()
    const logEntry = useScentingLogEntry(logEntryId)

    const handleSubmit = async (
        value: CreateInNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        try {
            const res = await workflow.run(
                () =>
                    create.mutateAsync(
                        // Gắn dòng sổ vào phiếu ngay trong request tạo: server
                        // làm cả hai trong một transaction nên không bao giờ có
                        // phiếu mà sổ vẫn báo "chưa lập phiếu".
                        logEntryId
                            ? { ...value, log_entry_id: logEntryId }
                            : value,
                    ),
                (result) => result.data?.id,
            )
            const noteId = res.data?.id
            if (noteId)
                void navigate({
                    to: '/scenting-warehouse/stock-in-notes/$id',
                    params: { id: noteId },
                })
        } catch (err) {
            toast.error(
                err instanceof ApiError ? err.message : m.sl_new_error_create(),
            )
        }
    }

    // Chờ dữ liệu sổ về rồi mới dựng form: react-hook-form chỉ đọc defaultValues
    // một lần, render form rỗng trước là mất luôn dữ liệu điền sẵn.
    if (logEntryId && logEntry.isLoading) {
        return <div className="p-6">{m.scl_log_prefill_loading()}</div>
    }

    const entry = logEntry.data?.data
    const prefill = entry ? logEntryToFormValues(entry) : undefined

    return (
        <ScentingLotStockInForm
            isPending={create.isPending}
            initialValues={prefill as never}
            seedSourceOptions={
                entry ? logEntryToSourceOptions(entry) : undefined
            }
            onSubmit={handleSubmit}
            onCancel={() =>
                navigate({
                    to: '/scenting-warehouse/stock-in-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

// TẠM ẨN phiếu lấy hàng khỏi UI (tính năng item-level-lot-lineage chưa hoàn thiện).
// Đặt = true để bật lại picker phiếu lấy hàng trong form. Không xoá code/BE.
const WITHDRAWAL_UI_ENABLED = false

export function ScentingLotStockInForm({
    mode = 'create',
    initialValues,
    initialWithdrawalNoteId,
    title,
    submitLabel,
    seedLotOptions,
    seedSourceOptions,
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    /** Phiếu lấy hàng đã chọn khi tạo — dùng để sửa pre-select lại picker. */
    initialWithdrawalNoteId?: string
    title?: string
    submitLabel?: string
    seedLotOptions?: LotOpt[]
    /** Lô phân loại nguồn đã chọn (để sửa hiện đúng dù note đã trừ cạn). */
    seedSourceOptions?: LotOpt[]
    isPending: boolean
    onSubmit: (
        value: CreateInNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => void
    onCancel: () => void
}) {
    const workflow = useWorkflowTracking('scenting_stock_in', {
        operation: mode === 'edit' ? 'update' : 'create',
    })
    const isEdit = mode === 'edit'
    const { data: gradesData } = useTeaGradesList(undefined, 'scenting')
    // Ô "lô có sẵn": tìm server + cuộn tải thêm (nguồn lô dùng chung); lô
    // của phiếu đang sửa làm seed để luôn hiện được.
    const lotSource = useScentingLotSource({ seed: seedLotOptions })

    // Gợi ý theo phiếu lấy hàng kho phân loại (tuỳ chọn). Search theo tiêu đề,
    // debounce qua API, cuộn tải thêm. Chọn note → picker lô nguồn chỉ hiện lô
    // trong note; không lưu gì, chỉ trừ số dư note khi tạo (BE lo).
    const [withdrawalNoteId, setWithdrawalNoteId] = useState(
        initialWithdrawalNoteId ?? '',
    )
    const [withdrawalSearch, setWithdrawalSearch] = useState('')
    const debouncedWithdrawalSearch = useDebounced(withdrawalSearch, 300)
    const withdrawalNotesQuery = useSortingWithdrawalNotesInfinite({
        per_page: 20,
        status: 'open',
        search: debouncedWithdrawalSearch || undefined,
    })
    const withdrawalNoteOptions = useMemo(
        () =>
            (withdrawalNotesQuery.data?.pages ?? [])
                .flatMap((p) => p.data ?? [])
                .map((n) => ({
                    value: n.id,
                    label: `${n.notes ?? n.code} · ${n.code}`,
                })),
        [withdrawalNotesQuery.data],
    )
    const { data: pickedNoteData } = useSortingWithdrawalNote(
        withdrawalNoteId || undefined,
    )
    const noteSourceOptions = useMemo<LotOpt[]>(
        () =>
            (pickedNoteData?.data?.items ?? [])
                .filter((it) => it.quantity > 0) // đã rút cạn thì bỏ
                .map((it) => ({
                    value: it.sorting_lot_id,
                    label: `${it.sorting_lot_code ?? it.sorting_lot_id} — ${it.grade_name ?? ''} (${m.scl_new_source_remaining({ qty: formatQty(it.quantity) })})`,
                    available: it.quantity,
                    unit: 'kg',
                })),
        [pickedNoteData],
    )

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'from_process',
            scenting_date: todayYMD(),
            notes: '',
            items: [
                { kind: 'new' as const, new_lot_grade_id: '', quantity: 0 },
            ],
        },
    })

    const reason = useWatch({ control: form.control, name: 'reason' })

    const gradeOptions = useMemo(
        () =>
            (gradesData?.data ?? []).map((g) => ({
                value: g.id,
                label: g.name,
            })),
        [gradesData],
    )
    // Chọn note → options cố định theo lô trong note (lọc client). Không chọn →
    // tìm tự do toàn kho phân loại (tìm server + cuộn tải thêm). Luôn merge seed
    // (lô đã chọn khi sửa) để hiện đúng dù lô đã bị trừ cạn khỏi note.
    const usingNote = !!withdrawalNoteId
    const sourceLotSource = useSortingLotSource({
        filters: { only_available: '1' },
        seed: seedSourceOptions,
        enabled: !usingNote,
    })
    const sourceLotOptions = useMemo<LotOpt[]>(() => {
        if (!usingNote) return sourceLotSource.options
        const map = new Map<string, LotOpt>()
        for (const opt of seedSourceOptions ?? []) map.set(opt.value, opt)
        for (const opt of noteSourceOptions) map.set(opt.value, opt) // số dư mới
        return Array.from(map.values())
    }, [
        usingNote,
        noteSourceOptions,
        sourceLotSource.options,
        seedSourceOptions,
    ])

    useEffect(() => {
        const subscription = form.watch((_values, { name }) => {
            if (!name) return
            workflow.interact(name.startsWith('items') ? 'items' : 'details')
            if (name.endsWith('_lot_id') || name === 'reason')
                workflow.step('selection_changed')
        })
        return () => subscription.unsubscribe()
    }, [form, workflow])

    const submit = form.handleSubmit(
        (data) => {
            const isChain = data.reason === 'from_process'
            const items = buildScentingInItems(data.items, isChain)
            onSubmit(
                {
                    reason: data.reason,
                    scenting_date: data.scenting_date,
                    source_withdrawal_note_id:
                        isChain && withdrawalNoteId
                            ? withdrawalNoteId
                            : undefined,
                    notes: data.notes,
                    items,
                },
                workflow,
            )
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <PageBackButton onBack={onCancel} />
                    <h1 className="text-xl font-semibold">
                        {title ?? m.scl_new_pnk_title()}
                    </h1>
                </div>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    noValidate
                    className="space-y-6 max-w-4xl"
                >
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4 [&>*]:min-w-0">
                        <div>
                            <Label>{m.sl_new_reason_in()}</Label>
                            <select
                                className="mt-1 h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60"
                                disabled={isEdit}
                                {...form.register('reason')}
                            >
                                <option value="from_process">
                                    {m.scl_new_reason_in_from_pxk()}
                                </option>
                                <option value="return_from_packaging_process">
                                    {m.scl_in_reason_return_from_packaging_process()}
                                </option>
                                <option value="adjustment">
                                    {m.sl_new_reason_in_adjustment()}
                                </option>
                            </select>
                        </div>
                        <Field
                            name="scenting_date"
                            label={m.scl_new_scenting_date_label()}
                            required
                            className="space-y-0"
                        >
                            <Input
                                id="scenting_date"
                                type="date"
                                className="mt-1"
                                {...form.register('scenting_date')}
                            />
                        </Field>
                        <div>
                            <Label>{m.sl_new_notes()}</Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                        {WITHDRAWAL_UI_ENABLED && reason === 'from_process' && (
                            <div className="md:col-span-2">
                                <Label className="mb-1 block">
                                    {m.scl_new_withdrawal_note_label()}
                                </Label>
                                <Combobox
                                    options={[
                                        {
                                            value: '',
                                            label: m.scl_new_withdrawal_note_none(),
                                        },
                                        ...withdrawalNoteOptions,
                                    ]}
                                    value={withdrawalNoteId}
                                    onChange={(v) => {
                                        setWithdrawalNoteId(v)
                                        // Đổi phiếu → xoá lô nguồn đã chọn để
                                        // buộc chọn lại theo phiếu mới (tránh
                                        // gửi lô của phiếu cũ → trừ trật).
                                        const items =
                                            form.getValues('items') ?? []
                                        items.forEach((_, i) => {
                                            form.setValue(
                                                `items.${i}.sources`,
                                                [],
                                            )
                                        })
                                    }}
                                    onSearchChange={setWithdrawalSearch}
                                    shouldFilter={false}
                                    selectedOption={
                                        withdrawalNoteId && pickedNoteData?.data
                                            ? {
                                                  value: pickedNoteData.data.id,
                                                  label: `${pickedNoteData.data.notes ?? pickedNoteData.data.code} · ${pickedNoteData.data.code}`,
                                              }
                                            : undefined
                                    }
                                    onOpenChange={(open) => {
                                        if (!open) setWithdrawalSearch('')
                                    }}
                                    onLoadMore={() =>
                                        void withdrawalNotesQuery.fetchNextPage()
                                    }
                                    hasMore={withdrawalNotesQuery.hasNextPage}
                                    placeholder={m.scl_new_withdrawal_note_placeholder()}
                                />
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {m.scl_new_source_hint()}
                                </p>
                            </div>
                        )}
                    </div>

                    <LotItemsField
                        mode="in"
                        lotSource={lotSource}
                        // Schema lấy hương kiểm `scenting_lot_id`; thiếu prop này
                        // LotItemsField ghi vào `sorting_lot_id` mặc định → submit
                        // bị chặn mà không hiện lỗi.
                        existingIdField="scenting_lot_id"
                        categoryOptions={gradeOptions}
                        categoryLabel={m.sl_lot_grade()}
                        allowNewLot={reason !== 'return_from_packaging_process'}
                        allowSplit
                        splitFieldName="splits"
                        renderExtraPerItem={
                            reason === 'from_process'
                                ? (idx) => (
                                      <LotSourcesSubTable
                                          idx={idx}
                                          options={sourceLotOptions}
                                          onSearchChange={
                                              sourceLotSource.onSearchChange
                                          }
                                          clientFilter={usingNote}
                                          onLoadMore={
                                              usingNote
                                                  ? undefined
                                                  : sourceLotSource.onLoadMore
                                          }
                                          hasMore={sourceLotSource.hasMore}
                                          loadingMore={
                                              sourceLotSource.loadingMore
                                          }
                                          labels={{
                                              title: m.scl_new_sources_title(),
                                              empty: m.scl_new_sources_empty(),
                                              add: m.scl_new_source_add(),
                                              remove: m.scl_new_source_remove(),
                                              lotPlaceholder:
                                                  m.scl_new_source_lot_placeholder(),
                                              qtyLabel:
                                                  m.scl_new_source_qty_label(),
                                          }}
                                      />
                                  )
                                : undefined
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ??
                                (isPending
                                    ? m.sl_new_submitting()
                                    : m.sl_new_submit_pnk())}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
